import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { User } from '../api/entities.js';
import { UserFavorite } from '../api/entities.js';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Button } from "../components/ui/button.jsx";
import { Skeleton } from "../components/ui/skeleton.jsx";
import { Input } from "../components/ui/input.jsx";
import { 
  Ticket, 
  Building2, 
  FolderKanban, 
  BookOpen, 
  Cpu, 
  Star, 
  Search,
  Trash2,
  ExternalLink
} from 'lucide-react';
import { ScrollArea } from "../components/ui/scroll-area.jsx";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu.jsx";

export default function Favorites() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const userData = await User.me();
      setUser(userData);
      
      const favoritesData = await UserFavorite.filter({ user_id: userData.id });
      setFavorites(favoritesData);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
    setIsLoading(false);
  };

  const handleRemoveFavorite = async (id) => {
    try {
      await UserFavorite.delete(id);
      setFavorites(favorites.filter(fav => fav.id !== id));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const getItemIcon = (type) => {
    switch (type) {
      case 'ticket':
        return <Ticket className="w-5 h-5" />;
      case 'client':
        return <Building2 className="w-5 h-5" />;
      case 'project':
        return <FolderKanban className="w-5 h-5" />;
      case 'knowledge':
        return <BookOpen className="w-5 h-5" />;
      case 'system':
        return <Cpu className="w-5 h-5" />;
      default:
        return <Star className="w-5 h-5" />;
    }
  };

  const getItemUrl = (item) => {
    switch (item.item_type) {
      case 'ticket':
        return createPageUrl(`ViewTicket?id=${item.item_id}`);
      case 'client':
        return createPageUrl(`ViewClient?id=${item.item_id}`);
      case 'project':
        return createPageUrl(`ViewProject?id=${item.item_id}`);
      case 'knowledge':
        return createPageUrl(`ViewKnowledge?id=${item.item_id}`);
      case 'system':
        return createPageUrl(`ViewSystem?id=${item.item_id}`);
      default:
        return '#';
    }
  };

  const filteredFavorites = searchTerm 
    ? favorites.filter(fav => 
        fav.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (fav.description && fav.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : favorites;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Meus Favoritos</h1>
        <p className="text-muted-foreground mt-1">Acesse rapidamente seus itens favoritos</p>
      </div>

      <div className="flex gap-4">
        <Input
          placeholder="Buscar favoritos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
          icon={<Search className="h-4 w-4" />}
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Skeleton key={item} className="h-[140px] w-full" />
          ))}
        </div>
      ) : filteredFavorites.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Star className="h-12 w-12 mx-auto text-muted-foreground opacity-50 mb-3" />
            <h3 className="text-lg font-medium">Nenhum favorito encontrado</h3>
            <p className="text-muted-foreground mt-2">
              {searchTerm 
                ? "Nenhum favorito corresponde à sua busca." 
                : "Você pode adicionar itens aos favoritos para acesso rápido."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFavorites.map((favorite) => (
            <Card key={favorite.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2 cursor-pointer hover:text-primary transition-colors" 
                       onClick={() => navigate(getItemUrl(favorite))}>
                    {getItemIcon(favorite.item_type)}
                    <span className="truncate">{favorite.name}</span>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="ml-auto">
                        <Star className="h-4 w-4 text-yellow-500" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        onClick={() => navigate(getItemUrl(favorite))}
                        className="cursor-pointer"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        <span>Abrir</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleRemoveFavorite(favorite.id)}
                        className="cursor-pointer text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>Remover dos Favoritos</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-muted-foreground">
                  {favorite.description || 
                    `Item do tipo ${favorite.item_type === 'ticket' ? 'chamado' : 
                    favorite.item_type === 'client' ? 'cliente' : 
                    favorite.item_type === 'project' ? 'projeto' : 
                    favorite.item_type === 'knowledge' ? 'artigo' : 
                    favorite.item_type === 'system' ? 'sistema' : 'item'}`
                  }
                </div>
                <div className="flex justify-end mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleRemoveFavorite(favorite.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Remover
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}